<template>
    <div>
        <p v-if="error" class="text-danger">{{ errorMsg }}</p>

            <div class="d-flex flex-column align-items-center">
                
                <div class="w-100 max-width-1000">

                    <div class="mb-4 w-100">
                        <h5>Summary</h5>
                        <p class="text-muted w-100">
                            Use this to write your complete RFx document, or just write a short summary describing what you are sourcing.
                        </p>
                        
                        <quill-editor-full 
                            ref="textContentComponent"
                            v-model="content" 
                            :disabled="(updateContentBusy && !quietModeUpdate) || allDisabled" 
                            :placeholder="updateContentBusy ? 'Loading...' : 'Write RFX content here'" 
                            class="mb-3 shadow rounded-lg border overflow-hidden w-100" 
                            content-height="500px"
                            toolbar="standard"
                            :loading="updateContentBusy && !quietModeUpdate"
                            snippet-area="rfx_content"
                            @input="debouncedSave"
                            @input.once="setContentLastSaved"
                        >
                        </quill-editor-full>

                        <div class="text-center w-100">
                            <tri-state-save-button
                                button-size="md"
                                class="rounded-pill"
                                min-width-string="180px"
                                variant="primary"
                                :loading="updateContentBusy"
                                :no-changes="!hasUnsavedContent"
                                @click="updateRfxContent(true)"
                            ></tri-state-save-button>
                        </div>
                    </div>

                    <hr>

                    <div class="w-100">
                        <h5>Attachments</h5>
                        <p class="text-muted w-100">
                            Upload any attachment -- detailed documents, drawings, layouts, CAD files, etc.  Will be available for vendors to download.
                        </p>
                        <base-s-3-file-list
                            list-endpoint="rfx.getFiles"
                            :parent-id="rfxId"
                            :disabled="allDisabled"
                            file-category="rfx_files"
                            table-card-class="shadow"
                        ></base-s-3-file-list>
                    </div>

                </div>

                

            </div>
    </div>
</template>

<script>
import { errorMessageMaker } from '../js/filters-helpers';
import { suppliosApi } from '../js/api-service';
import debounce from 'lodash/debounce';
import { TriStateSaveButton } from '../js/simple-vue-components';
import { Rfx } from '../js/models/rfx';

export default {
    name: 'RfxContent',
    components: {
        TriStateSaveButton,
    },
    props: {
        rfxId: {
            type: [String, Number],
            required: true,
        },
        isReadOnly: {
            type: Boolean,
            default: false,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            currentTabIndex: null,  // v-model for b-tabs, current tab index starting with 0
            currentTabRef: '',  // updated to the ref of currently active tab
            attachmentCount: null,
            content: '',
            contentLastSaved: '',  // stash content here that we know was last saved
            rfxData: {},  // don't need much of it, but we'll have it anyways
            //initialContentLoaded: false,  // set this to TRUE on the FIRST @input event from quill editor, this blocks the debouncedSave if false
            quietModeUpdate: false, // set TRUE when we want to do do 'quiet' updates without extra stuff
            updateContentBusy: false,  // loading flag for updating quill content
            trySave: false,
            saveResponseStatus: {
                default: null,
                type: Number
            }
        }
    },
    computed: {
        hasUnsavedContent: function() {
            // returns Boolean to show if CONTENT (text editor) has any unsaved changes (not other tabs)
            // use method you have below to check all the tabs
            return this.content !== this.contentLastSaved;
        },
        allDisabled() {
            // return boolean, if all disabled because event it archived, etc.
            // NOV 9 2022 - also if viewer is not admin (we allow decision team to view in some situations)
            return this.isReadOnly || (this.rfxData.sm_state === 'archived');
        },
    },
    created: function() {
        this.debouncedSave = debounce(this.updateRfxContentQuiet, 2000);
    },
    mounted: function() {
        this.getRfxContent();
    },
    methods: {
        saveAll: function() {
            // PUBLIC method called from parent, part of Setup Wizard
            // should return a single PROMISE that resolves to Boolean T/F
            // should also handle overall validation and show errors if any, if errors return Promise.resolve(false)
            // need to save tab first, then do any overall validation... like checking that we have SOMETHING in content
            // saveTab of the content tab doesn't do this... because we want to allow saving empty content... so we check for content below
            // NOTE:  must always save 'Content' tab... even if attachements or other tab is active -- do that in this.saveTab()
            // NOTE:  current way of doing this does not save tabs that are not active (except for content tab itself) because lazy is set, and that completely removes the components
            // NEW Nov 21 2021 
            if (this.allDisabled) {  // if this is disabled because of archived, etc
                return {
                    statusPromise: Promise.resolve(false),
                    successMessage: 'Event is archived - no changes allowed',
                }
            }
            let tabResult = this.saveTab(this.currentTabIndex);  // this is a PROMISE that resolves to Boolean
            let saveSuccessPromise = tabResult.then(tabSuccess => {
                if (!tabSuccess) {
                    // if saveTab failed, just return false here (this returns a Promise that resolves to false, which is what we want)
                    return false;
                }
                // if all tab save was OK, now check for content being not empty
                if (!this.content) {
                    this.handleError(null, "You must add something in 'Content' -- at least a short summary of instructions for suppliers.");
                    return false;
                }
                // if we made it here, then saveTab was OK, and content was not empty, so return true in this Promise
                this.error = false;  // clear any errors if we had one before
                this.showSaveSuccess();
                return true;
            });  // overall this will return a Promise that resolves to true / false
            return {
                statusPromise: saveSuccessPromise,
                successMessage: 'All content saved',
            };
        },
        okToContinue: function() {
            // PUBLIC METHOD CALLED FROM PARENT - takes tabChangeEvent as arg, return Promise that resolves to boolean where true = can continue
            console.log('from content component okToContinue');
            let hasUnsaved = this.checkHasUnsavedChanges();
            if (!hasUnsaved) return Promise.resolve(true);  // if no unsaved changes, return Promise that resolves to true
            // otherwise...
            let confirmMessage = 'You have unsaved changes on this page.  Are you sure you want to leave?';
            return this.$bvModal.msgBoxConfirm(confirmMessage, {
                title: 'Discard Unsaved Changes?',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'Yes, Discard Changes',
                cancelTitle: 'No, Cancel',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            });  // just return this directly... will be Promise that resolves to true if click OK, false otherwise
        },
        showSaveSuccess: function(msg) {
            // separating out save success message toast for DRY
            let successMsg = msg ? msg : 'Content saved';
            this.$toast.success(successMsg);
        },
        /// Public methods above 
        setContentLastSaved: function(content) {
            // gets called ONCE from quill editor, save this to contentLastSaved
            // have to do it this way since quill editor may SLIGHTLY change the content once its loaded... like going from double spaces to single spaces after periods
            // we also use this to ignore the first @input event for the debounced save call....
            //this.initialContentLoaded = true;
            //console.log("Setting contentLastSaved inside setContentLastSaved, new content below:");
            //console.log(content);
            this.contentLastSaved = content;
        },
        checkHasUnsavedChanges: function() {
            // checks all necessary things for any unsaved changes - returns TRUE if any unsaved changes
            // call this from okToContinue
            let contentComponentUnsaved = this.hasUnsavedContent;
            let anyUnsaved = contentComponentUnsaved;  // you had more before...
            //console.log('Unsaved changes in content?');
            //console.log(anyUnsaved);
            return anyUnsaved;
        },
        reportActiveRef: function() {
            console.log('currentTabIndex is ' + this.currentTabIndex);
            console.log('current active tab ref is:');
            console.log(this.$refs.tabsComponent.tabs[this.currentTabIndex]);
        },
        handleTabsInput: function(event) {
            console.log('Dumping input event from b-tabs');
            console.log(event);
            this.reportActiveRef();
        },
        saveTab: function(tabIndex) {
            // calls relevant 'save' method on the main component of specified tab, if any relevant save method
            // returns PROMISE that resolves to Boolean if save successful
            // tabIndex is the tab you want to save -- will be 0, 1, 2
            // need to hardcode tabIndex to a specific component... maybe there is a better way, but this is easy
            // NOTE:  Always should save 'Content' tab, even if other tab is active... use Promise.all([promise1, promise2]) to return a single promise that has array of values, then logic to combine them into a single Boolean
            // UPDATE Feb 2021 - you removed tabs in this component... keeping this until you know its gone for good.  For now, this will still get called, will go to default case, just trigger save on text-content
            let saveSuccess;
            switch (tabIndex) {
                case 0:  // handle ref="textContentComponent" - this save happens locally in this component, not in child component
                    saveSuccess = this.updateRfxContent(); // returns promise, resolves to Boolean based on save success
                    break;
                case 1:  // handle ref="attachmentsComponent" which is base-attachment-list component  (its gone now... change this???)
                    // there's nothing to save for attachments... so just save Content
                    // also have to save 'Content'!  so just call that and return its promise
                    saveSuccess = this.updateRfxContent();
                    break;
                default:
                    //saveSuccess = Promise.resolve(true);
                    saveSuccess = this.updateRfxContent();
                    break;
            }
            return saveSuccess;  // a Promise that resolves to Boolean
        },
        handleError: function(error, message) {
            console.log(message);
            console.log(error);
            this.errorMsg = errorMessageMaker(message);
            this.error = true;
        },
        handleAttachmentCountChange: function(attachmentCount) {
            this.attachmentCount = attachmentCount;
        },
        getRfxContent: function() {
            this.updateContentBusy = true;
            suppliosApi.rfx.retrieve(this.rfxId)
                .then(response => {
                    console.log(response);
                    this.content = response.data.text_content;
                    // don't save stashed content here... you'll do it by watching first @input emit from editor, since editor may slightly manipulate things when content loaded
                    this.rfxData = new Rfx(response.data);
                })
                .catch(error => this.handleError(error, 'Error while getting RFX Content'))
                .finally(() => this.updateContentBusy = false);
        },
        updateRfxContentQuiet: function() {
            // force quietMode on
            if (!this.hasUnsavedContent) return;  // don't do the save if there are no changes to save
            return this.performUpdateContent(true, false);
        },
        updateRfxContent: function(showToast) {
            // showToast is Boolean, if called from parent that shows its own toasts, then set to false, otherwise set to true
            return this.performUpdateContent(false, showToast);
        },
        performUpdateContent: function(quietMode, showToast) {
            // first arg: (quietMode) if true then no toast is shown and no loading is shown
            // second arg:  if true, then toast is shown (but not if quiet mode is true)
            this.quietModeUpdate = quietMode ? true : false;
            this.updateContentBusy = true;
            let updateData = {text_content: this.content};
            return suppliosApi.rfx.partialUpdate(this.rfxId, updateData)
                .then(response => {
                    this.saveResponseStatus = response.status;
                    this.setContentLastSaved(response.data.text_content);  // stash last saved -- ok here (not in get) because we should be getting back exactly what we sent.
                    console.log(response);
                    if (!quietMode && showToast) this.$toast.success(`The content has been updated.`);
                    return true;  // passed to next function in promise chain
                })
                .catch(error => {
                    this.handleError(error, 'Error while saving updates');
                    return false;
                })
                .then(saveSuccess => {
                    this.updateContentBusy = false;  // can set this to false no matter what we did at the start
                    this.quietModeUpdate = false;
                    return saveSuccess;  // return Boolean to next thing in promise chain, or caller of this function
                });
        }
    }
}
</script>

<style>

</style>